import { useState, useRef, useCallback } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SingleImageUploader = ({ image = "", onImageChange, label = "Imagine" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      toast.error("Fișierul nu este o imagine");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imaginea este prea mare (max 5MB)");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("files", file);

      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.uploaded?.length > 0) {
        const newImageUrl = response.data.uploaded[0].url;
        onImageChange(newImageUrl);
        toast.success("Imagine încărcată!");
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach((err) => toast.error(err));
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setUploading(false);
    }
  }, [onImageChange]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile]
  );

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const removeImage = () => {
    onImageChange("");
    toast.success("Imagine eliminată");
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("/api")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  return (
    <div className="space-y-2">
      {/* Image Preview */}
      {image && (
        <div className="relative w-full h-48 rounded-lg overflow-hidden bg-stone-100 group">
          <img
            src={getImageUrl(image)}
            alt={label}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button
              type="button"
              onClick={removeImage}
              className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {!image && (
        <div
          data-testid="single-image-upload-dropzone"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${
              dragActive
                ? "border-emerald-500 bg-emerald-50"
                : "border-stone-300 hover:border-stone-400 hover:bg-stone-50"
            }
            ${uploading ? "pointer-events-none opacity-50" : ""}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleInputChange}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
              <p className="text-stone-600 text-sm">Se încarcă...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mb-2">
                <ImageIcon className="w-6 h-6 text-stone-400" />
              </div>
              <p className="text-stone-700 font-medium text-sm mb-1">
                {dragActive ? "Eliberează pentru a încărca" : "Trage imaginea aici"}
              </p>
              <p className="text-stone-500 text-xs">
                sau click pentru a selecta • JPG, PNG, WebP • Max 5MB
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SingleImageUploader;
